import React, {Fragment, Suspense, useContext, useEffect, useState} from 'react';
import {Route, Switch, useLocation} from 'react-router-dom';

import {Sprite} from '@nur-portal/ui-kit';

import {publishEvent} from '../services/analytics/pub-sub';
import {ANALYTIC_EVENTS} from '../services/analytics/pub-sub/constants';
import {getAccessToken} from '../services/tokens';
import {USER_ROLES} from '../website-ui/constants/user';
import {useUser} from '../website-ui/hooks/use-user';

import Loader from './components/Loader';
import {ModalContext} from './contexts/ModalContext';
import {UserVotesContext} from './contexts/UserVotesContext';
import SideModal from './modals/SideModal';
import {
  JWT,
  MODAL_ADD_PHONE,
  MODAL_ERROR,
  RATING_API,
  SHOW_MODAL_FROM_LOGIN_PAGE
} from './constants';
import {mainSeoTexts} from './seoTemplates';

const NotFoundPage = React.lazy(() => import(/* webpackChunkName: "notfound" */'./NotFoundPage'));
const CategoryContent = React.lazy(() => import(/* webpackChunkName: "category" */'./components/CategoryContent'));
const FaqPage = React.lazy(() => import('./FaqPage'));

const NEWSLINE_TITLE = 'Новости проекта';
const ADDITIONAL_CATEGORIES = [
  {
    title: NEWSLINE_TITLE,
    slug: 'news'
  }
];

let isFisrtLoad = true;
const LOGIN_REFERRER = '/login';
const showedModalFromLoginPage = localStorage.getItem(SHOW_MODAL_FROM_LOGIN_PAGE);

export default function() {
  const user = useUser();
  const [categories, setCategories] = useState([]);
  const {setInitialUserVotes} = useContext(UserVotesContext);
  const [isLogged, setIsLogged] = useState(false);
  const [nurToken, setNurToken] = useState(getAccessToken());
  const [seoTexts, setSeoTexts] = useState(mainSeoTexts());
  const [isLoading, setIsLoading] = useState(true);
  const {setOpenModal} = useContext(ModalContext);
  const userHasPhoneNumber = user.role === USER_ROLES.EXTENDED;

  const {pathname} = useLocation();

  const getUserVotes = jwt => {
    fetch(`${RATING_API}votes`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`
        }
      })
      .then(res => {
        if (res.status !== 200) {
          throw new Error(`Couldn't fetch user votes. Status: ${res.status}`);
        }
        return res;
      })
      .then(res => res.json())
      .then(setInitialUserVotes)
      .catch(err => {
        setInitialUserVotes([]);
        return console.error(err);
      });
  };

  const loginByNur = token => {
    fetch(`${RATING_API}auth/hive`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token
        })
      }
    )
      .then(res => {
        if (res.status !== 200) {
          throw new Error(`Couldn't login to Strapi. Status: ${res.status}`);
        }
        return res;
      })
      .then(res => res.json())
      .then(res => {
        if (res?.jwt) {
          localStorage.setItem(JWT, res?.jwt);
          setIsLogged(true);
          return res.jwt;
        }
        throw new Error(`Couldn't login to Strapi. Status: ${res.status}`);
      })
      .catch(console.error);
  };

  useEffect(() => {
    setIsLoading(true);
    fetch(`${RATING_API}categories?sort[0]=id:asc`)
      .then(res => {
        if (res.status !== 200) {
          res.json()
            .then(data => {
              throw new Error(data.message);
            })
            .catch(console.error);
          throw new Error('Something wrong on fetching Categories!');
        }
        return res;
      })
      .then(response => response.json())
      .then(data => {
        setCategories([
          ...data.filter(category => category.nominations.length),
          ...ADDITIONAL_CATEGORIES
        ]);
        return data;
      })
      .catch(err => {
        setCategories([]);
        setOpenModal({
          isOpen: true,
          type: MODAL_ERROR,
          params: {
            heading: 'Не удалось загрузить категории',
            text: 'Попробуйте обновить страницу'
          }
        });
        return console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isLogged && nurToken) {
      return getUserVotes(localStorage.getItem(JWT));
    }
  }, [isLogged, nurToken]);

  useEffect(() => {
    if (nurToken) {
      loginByNur(getAccessToken());
    }
  }, [nurToken]);

  useEffect(() => {
    setNurToken(getAccessToken());
  }, [user]);

  useEffect(() => {
    if (
      window.document.referrer.includes(LOGIN_REFERRER)
      && !userHasPhoneNumber
      && !showedModalFromLoginPage
      && user.isAuthenticated
    ) {
      setOpenModal({
        isOpen: true,
        type: MODAL_ADD_PHONE
      });
      localStorage.setItem(SHOW_MODAL_FROM_LOGIN_PAGE, true);
    }
  }, [user.isAuthenticated, userHasPhoneNumber]);

  useEffect(() => {
    if (isFisrtLoad) {
      isFisrtLoad = false;
    } else {
      publishEvent(ANALYTIC_EVENTS.PAGEVIEW);
    }
  }, [pathname]);

  if (isLoading) {
    return <Loader />;
  }
  if (!categories.length) {
    return null;
  }

  return (
    <Fragment>
      <Suspense fallback={<Loader />}>
        <Switch>
          <Route
            exact
            path='/not-found/'
            component={NotFoundPage}
          />
          <Route
            exact
            path='/faq/'
            render={() => <FaqPage />}
          />
          <Route
            exact
            path={['/', '/:category', '/:category/:nomination', '/:category/:nomination/:candidate']}
            render={() => (
              <CategoryContent
                seoTexts={seoTexts}
                setSeoTexts={setSeoTexts}
                categories={categories}
              />
            )}
          />
        </Switch>
        <Route
          exact
          path='/:category/:nomination/:candidate'
          render={() => <SideModal seoTexts={seoTexts} setSeoTexts={setSeoTexts} categories={categories} />}
        />
      </Suspense>
      <Sprite />
    </Fragment>
  );
}
